import { defaultCharacterStyle, type CharacterStyle } from '../core/characters';
import { characterGroups, grippingHandGroups } from '../render/characterPaint';
import { buildFigure, emptyLimbState } from '../render/figure';
import { PRIM_CAPSULE, PRIM_DISK, PRIM_ROUNDED_BOX, type Primitive } from '../render/sdf/primitives';
import { placeWeapon, weaponLook, WEAPON_INK } from '../render/weaponShapes';
import { WEAPON_BY_ID } from '../sim/weapons/defs';
import type { PartyPresetId } from './partyPresets';

const n = (value: number) => Math.round(value * 1000) / 1000;

/** The same silhouettes as the arena, composed into small, readable cover illustrations. */
function paint(primitives: Primitive[], color: string): string {
  return `<g fill="${color}">${primitives.map((p) => {
    const transform = p.rot ? ` transform="rotate(${n(p.rot * 180 / Math.PI)} ${n(p.ax)} ${n(p.ay)})"` : '';
    if (p.kind === PRIM_DISK) return `<circle cx="${n(p.ax)}" cy="${n(p.ay)}" r="${n(p.r)}"/>`;
    if (p.kind === PRIM_CAPSULE) return `<path d="M${n(p.ax)} ${n(p.ay)}L${n(p.bx)} ${n(p.by)}" fill="none" stroke="${color}" stroke-width="${n(p.r * 2)}" stroke-linecap="round"/>`;
    if (p.kind === PRIM_ROUNDED_BOX) return `<rect x="${n(p.ax - p.bx)}" y="${n(p.ay - p.by)}" width="${n(p.bx * 2)}" height="${n(p.by * 2)}" rx="${n(p.r)}"${transform}/>`;
    return `<path d="M${n(p.ax - p.bx)} ${n(p.ay)}L${n(p.ax + p.bx)} ${n(p.ay)}L${n(p.ax)} ${n(p.ay + p.by)}Z"${transform}/>`;
  }).join('')}</g>`;
}

function weapon(id: string, x: number, y: number, scale: number, rotate = 0): string {
  const def = WEAPON_BY_ID.get(id)?.def;
  if (!def) return '';
  const look = weaponLook(def.shape.kind, def.shape.length);
  return `<g transform="translate(${x} ${y}) rotate(${rotate}) scale(${scale} ${-scale})">${paint(look.body, WEAPON_INK)}${paint(look.accent, look.accentColor)}</g>`;
}

function fighter(x: number, y: number, scale: number, color: string,
  options: { facing?: number; rotate?: number; weapon?: string; jumping?: boolean; style?: Partial<CharacterStyle> } = {}): string {
  const facing = options.facing ?? 1;
  const style = { ...defaultCharacterStyle(), ...options.style };
  const def = options.weapon ? WEAPON_BY_ID.get(options.weapon)?.def : undefined;
  const fig = buildFigure({ x: 0, y: 0, facing, ducking: false, grounded: !options.jumping,
    wallSliding: false, vx: options.jumping ? facing * 5 : 0, vy: options.jumping ? 5 : 0,
    aimX: facing, aimY: .2, punching: !def, blocking: false, dead: false, phase: .4,
    personality: style.personality, reducedMotion: true,
    weapon: def ? { length: def.shape.length, twoHanded: def.twoHanded, kind: def.shape.kind, category: def.category } : null,
  }, emptyLimbState());
  let art = characterGroups(fig, color, style).map((group) => paint(group.primitives, group.color)).join('');
  if (def) {
    const look = weaponLook(def.shape.kind, def.shape.length), a = fig.weaponAnchor;
    art += paint(placeWeapon(look.body, a.x, a.y, a.angle, a.flip), WEAPON_INK)
      + paint(placeWeapon(look.accent, a.x, a.y, a.angle, a.flip), look.accentColor)
      + grippingHandGroups(fig, color, style).map((group) => paint(group.primitives, group.color)).join('');
  }
  return `<g transform="translate(${x} ${y}) rotate(${options.rotate ?? 0}) scale(${scale} ${-scale})">${art}</g>`;
}

function pine(x: number, y: number, scale: number, color: string): string {
  return `<path fill="${color}" transform="translate(${x} ${y}) scale(${scale})" d="M-4 0V-40H-27L-14-63H-23L0-103L23-63H14L27-40H4V0Z"/>`;
}

function burst(x: number, y: number, scale = 1, color = '#fff1b7'): string {
  return `<path fill="${color}" transform="translate(${x} ${y}) scale(${scale})" d="M0-25L5-9L19-18L12-3L28 1L11 7L18 21L3 12L-5 28L-8 9L-25 15L-13 0L-26-10L-8-8L-9-26L0-14Z"/>`;
}

const illustrations: Record<PartyPresetId, () => string> = {
  classic: () => `
    <path fill="#80bfa2" d="M0 0H360V176H0Z"/>
    <circle fill="#c8e7b0" cx="246" cy="40" r="27"/>
    <path fill="#64a78b" d="M0 99Q51 39 110 84T242 74T360 65V176H0Z"/>
    ${pine(29, 159, 1.55, '#346a59')}${pine(66, 159, .94, '#427e69')}
    ${pine(307, 152, 1.38, '#346a59')}${pine(345, 162, 1.86, '#295447')}
    <path fill="#282c30" d="M54 143H248L259 154L245 176H44Z"/><path fill="#71b970" d="M54 141H247L255 148H47Z"/>
    ${fighter(141, 105, 44, '#f4bd51', { style: { accessory: 'headband' } })}
    ${burst(195, 90, .63)}
    ${fighter(240, 72, 39, '#83c9e8', { facing: -1, jumping: true, rotate: 25 })}
    <path d="M259 112L271 120M265 103L282 108" stroke="#c1e4b8" stroke-width="3" stroke-linecap="round"/>`,
  'arms-race': () => `
    <path fill="#b9a180" d="M0 0H360V176H0Z"/>
    <path fill="#e9c789" d="M178 0H260L352 176H90Z"/>
    <path fill="#786852" d="M0 0H32V140H0ZM329 0H360V176H329Z"/>
    <path fill="#8b765b" d="M160 176V110H224V66H284V23H333V176Z"/>
    <path d="M154 110H224M224 66H284M284 23H332" stroke="#f0d28e" stroke-width="5"/>
    ${weapon('pistol', 176, 83, 70, -18)}
    ${weapon('ak47', 241, 40, 62, -18)}
    ${weapon('rpg', 219, 145, 94, -13)}
    ${fighter(99, 110, 51, '#f4bd51', { weapon: 'god-pistol', style: { accessory: 'glasses' } })}
    <path fill="#f5eac1" d="M84 32L87 20L93 27L100 15L105 29L113 23L113 35Z"/>
    <path fill="#3d3734" d="M0 163H360V176H0Z"/>`,
  'office-brawl': () => `
    <path fill="#a4bcc5" d="M0 0H360V176H0Z"/>
    <path fill="#dae6df" d="M15 13H145V111H15Z"/><path fill="#bdcfcc" d="M15 89L55 46L79 70L109 36L145 82V111H15Z"/>
    <path d="M79 13V111M15 63H145" stroke="#7b9198" stroke-width="6"/>
    <path fill="#748f9c" d="M183 21H305V29H183Z"/>
    <path fill="#e5e6d6" d="M212 38H270V81H212Z"/><path d="M219 49H259M219 57H244M219 65H252" stroke="#b0bbb8" stroke-width="3"/>
    <path fill="#394654" d="M42 140H216V151H42ZM55 151H64V176H55ZM195 151H204V176H195Z"/>
    <path fill="#d5b58b" d="M36 136H220V143H36Z"/>
    ${weapon('office-monitor', 92, 109, 73)}${weapon('office-mug', 160, 123, 49)}
    ${weapon('office-stapler', 179, 70, 62, -28)}
    ${weapon('office-chair', 283, 95, 116, 21)}
    <path d="M265 33L256 21M274 30L271 14M298 139L309 151" stroke="#eff3dc" stroke-width="3" stroke-linecap="round"/>
    <path fill="#526672" d="M0 168H360V176H0Z"/>`,
  teams: () => `
    <path fill="#bb815d" d="M0 0H360V176H0Z"/><path fill="#56779a" d="M208 0H360V176H141Z"/>
    <path fill="#dca770" d="M0 70L161 0H201L102 176H0Z"/><path fill="#7899b5" d="M236 0H290L360 86V176H141Z"/>
    <path fill="#554750" d="M15 92V40H32V50H48V40H64V92ZM296 89V40H312V50H329V40H345V89Z"/>
    <path d="M65 81V15M293 84V15" stroke="#403b43" stroke-width="4"/>
    <path fill="#f6c269" d="M67 17H116L108 31L116 45H67Z"/><circle fill="#fff1ba" cx="87" cy="30" r="7"/>
    <path fill="#7bd0e8" d="M291 17H242L250 31L242 45H291Z"/><path fill="#e6f6f7" d="M267 22A9 9 0 1 0 277 37A9 9 0 0 1 267 22Z"/>
    <path fill="#393440" d="M0 147H145L151 176H0ZM216 147H360V176H207Z"/>
    <path d="M0 147H145M217 147H360" stroke="#d5bd93" stroke-width="5"/>
    ${fighter(113, 108, 47, '#f4bd51', { style: { accessory: 'headband' } })}
    ${fighter(246, 108, 47, '#83c9e8', { facing: -1, style: { accessory: 'wraps' } })}
    ${burst(180, 96, .65, '#fff0ca')}`,
  chaos: () => `
    <path fill="#74627f" d="M0 0H360V176H0Z"/>
    <path fill="#a87787" d="M0 0H360V67L0 151Z"/>
    <circle fill="#e8ac90" cx="212" cy="47" r="35"/>
    <path fill="#493f59" d="M0 150L74 133L98 176H0ZM273 144L350 131L360 176H286Z"/>
    <g fill="#473b50" stroke="#d29194" stroke-width="4"><path d="M22 71L82 53L89 75L30 94Z"/><path d="M257 54L307 67L301 89L249 77Z"/><path d="M169 149L211 138L218 158L177 170Z"/></g>
    ${fighter(145, 83, 46, '#f3c261', { jumping: true, rotate: -24, weapon: 'rpg' })}
    ${weapon('return-disc', 272, 35, 68, 18)}
    ${weapon('office-mug', 56, 123, 51, -24)}
    ${burst(246, 116, .77, '#f8c592')}
    <path d="M204 101L232 99M211 109L230 107M154 20L146 9M166 20L169 9M312 108L325 104" stroke="#f3cebd" stroke-width="3" stroke-linecap="round"/>`,
  pirate: () => `
    <path fill="#c89672" d="M0 0H360V176H0Z"/>
    <circle fill="#f0c98c" cx="241" cy="47" r="33"/>
    <path fill="#799b99" d="M0 123Q48 113 103 123T214 125T360 121V176H0Z"/>
    <path fill="#526f71" d="M0 146Q50 136 106 147T230 146T360 143V176H0Z"/>
    <path fill="#443831" d="M67 116L272 108L247 140H93Z"/>
    <path d="M72 117L270 109M171 111V10" stroke="#322e2c" stroke-width="5"/>
    <path fill="#303238" d="M165 19L162 92L94 91Q125 66 136 27ZM178 20L181 88L250 79Q218 58 212 19Z"/>
    <path fill="#d8cbaa" d="M166 13L136 5L141 20L164 27Z"/>
    <circle fill="#d9c7a6" cx="145" cy="58" r="10"/><path d="M140 68V73M149 68V73" stroke="#d9c7a6" stroke-width="4"/>
    <circle fill="#303238" cx="141" cy="56" r="2"/><circle fill="#303238" cx="149" cy="56" r="2"/>
    <path fill="#5b4031" d="M0 153H162V176H0Z"/><path fill="#b88c57" d="M0 151H162V157H0Z"/>
    <circle fill="#2c2f32" cx="75" cy="147" r="14"/><circle fill="#aa8051" cx="75" cy="147" r="6"/>
    <path d="M46 134L107 113" stroke="#282d30" stroke-width="24" stroke-linecap="round"/>
    <path d="M104 105L110 123" stroke="#707772" stroke-width="5"/>
    ${burst(133, 108, .7, '#ffda92')}
    <circle fill="#34343a" cx="193" cy="96" r="7"/><path d="M166 101L181 98" stroke="#e8c598" stroke-width="3"/>`,
  neon: () => `
    <path fill="#23394f" d="M0 0H360V176H0Z"/>
    <circle fill="#59628e" cx="227" cy="40" r="34"/>
    <path fill="#1b293e" d="M0 65H34V26H62V95H84V47H119V103H246V67H271V27H311V77H333V43H360V176H0Z"/>
    <path d="M40 36H55M40 46H55M40 56H55M279 39H302M279 51H302M339 57H352M339 69H352" stroke="#637d9a" stroke-width="3"/>
    <path fill="#b07ca5" d="M96 57H112V91H96Z"/><path d="M99 63H108M99 71H108M99 79H108" stroke="#f0aec4" stroke-width="2"/>
    <path fill="#111c2c" d="M0 145H165V176H0ZM243 121H360V176H243Z"/>
    <path d="M0 144H165M243 120H360" stroke="#60d9d9" stroke-width="4"/>
    <path d="M19 176L128 146M70 176L145 146M275 176L285 124M330 176L313 124M0 161H158M246 143H360" stroke="#2b566a" stroke-width="2"/>
    ${fighter(130, 109, 45, '#79cedc', { weapon: 'return-disc', style: { skin: 'circuit' } })}
    ${weapon('return-disc', 234, 70, 71, -14)}
    <path d="M198 89Q218 56 252 58M210 92Q234 103 253 83" fill="none" stroke="#81e4e4" stroke-width="3"/>
    <path d="M306 103V88M299 96H313M177 34V26M173 30H181" stroke="#d6b2de" stroke-width="2"/>`,
};

const cache = new Map<PartyPresetId, string>();

/** Decorative: the adjacent native button text names and describes the mode. */
export function presetIllustrationSvg(id: PartyPresetId): string {
  let svg = cache.get(id);
  if (!svg) {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 176" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">${illustrations[id]()}</svg>`;
    cache.set(id, svg);
  }
  return svg;
}
